import React from "react";
import { Box, CardContent, Typography } from "@mui/material";
import { PieChart as PieChartIcon } from "@mui/icons-material";
import { StandardCard } from "../../../../components/Cards";
import { Chart } from "../../../marketingCampaign/components/Graphs/chart";
import { BarGraphSkeleton } from "../skeletons";
import {
  IUpsellingDataItem,
  IServiceCategoryCard,
  isServiceCategoryCard,
} from "../../types/upsell.types";
import { SERVICE_CATEGORIES_DONUT_OPTIONS } from "../../constants/chartOptions";
import { NoRecordFound } from "../../../../components/NoRecordFound/NoRecordFound";

interface ServiceCategoriesDonutGraphProps {
  dashboardData: IUpsellingDataItem[] | null;
  height?: number | string;
}

export const ServiceCategoriesDonutGraph: React.FC<
  ServiceCategoriesDonutGraphProps
> = ({ dashboardData, height }) => {
  if (!dashboardData || dashboardData.length === 0) {
    return <BarGraphSkeleton />;
  }

  // Filter only service categories using type guard
  const serviceCategories = dashboardData.filter(
    isServiceCategoryCard,
  ) as IServiceCategoryCard[];

  const categories = serviceCategories.map((category) => ({
    name: category.service_category_name,
    leads: category.leads?.length || 0,
  }));

  // Filter out items with 0 leads to avoid clutter
  const nonZeroCategories = categories.filter((cat) => cat.leads > 0);

  const chartData = {
    series: nonZeroCategories.map((cat) => cat.leads),
    labels: nonZeroCategories.map((cat) => cat.name),
  };

  const chartOptions = {
    ...SERVICE_CATEGORIES_DONUT_OPTIONS,
    labels: chartData.labels,
  };

  return (
    <StandardCard>
      <CardContent sx={{ p: height ? 2 : 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <PieChartIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant="h5" fontWeight={600}>
            Service Categories Performance
          </Typography>
        </Box>
        <Box sx={{ height: height ?? 400, mt: 1 }}>
          {serviceCategories.length === 0 ? (
            <NoRecordFound />
          ) : (
            <Chart
              type="donut"
              series={chartData.series}
              options={chartOptions}
              height={height ?? 500}
            />
          )}
        </Box>
      </CardContent>
    </StandardCard>
  );
};
